"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.ActionStates = exports.useAsyncAction = void 0;
var react_1 = require("react");
/**
 * useAsyncAction hook returns a structure allowing you to track the state of an async and
 * it's return value.
 *
 * Usage:
 *
 * Invoke the hook and destructure it's return value (or keep it as an object if you prefer).
 * The returned object has 4 main properties:
 *
 * result:  The value of the resolved promise
 * state:   An instance of ActionState to track it's current status
 * trigger: A function you call when you want to invoke the async function. Simply pass
 *          the function to trigger. It will run it and update the status and result
 *          properties as required.
 * error:   If an error occurs is should be present in this property. It is not treated
 *          in anyway so it's type must be determined
 *
 * e.g.
 *
 * /// Triggering
 * const saveContactAction = useAsyncAction();
 *
 * saveContactAction.trigger(() => {
 *   return service.reallySlowMethod();
 * };
 *
 * /// Using status
 * return <div>
 *   {saveContactAction.state.slow && <p>Still processing!</p>}
 *   {saveContactAction.state.complete && <p>{saveContactAction.result}</p>}
 *   </div>
 *
 * Statuses:
 *
 * The state property tracks 4 main lifecycle booleans:
 *
 * triggered:  The action has been triggered. You might use this to prevent double click prevention
 *             by hiding the button in the UI
 * slow:       If the action is taking longer than a present time slow is flipped to true. This is
 *             used to show spinners or show processing messages.
 * complete:   True if the action has finished successfully.
 * failed:     True if the action threw some sort of error - which can be retrieved through the
 *             error property
 *
 * Debouncing:
 *
 * By default there is a 200ms delay before slow is set to true. This avoids 'flickering'
 * in spinners where the action is actually very fast. In those cases the 'flicker' often
 * leads the user to think the action failed, or in some cases aren't sure what they saw
 * and assume something has gone wrong.
 *
 * You can change this value by passing a different integer to the hook function. Zero removes
 * debouncing completely.
 *
 * // Wait longer
 * const saveContactAction = useAsyncAction(500);
 *
 * // Don't wait at all!
 * const saveContactAction = useAsyncAction(0);
 *
 * @param slowThreshold The number of milliseconds to wait before triggering the slow
 * state. Set to zero for no debounce.
 */
var useAsyncAction = function (slowThreshold) {
    if (slowThreshold === void 0) { slowThreshold = 200; }
    var _a = react_1.useState(), result = _a[0], setResult = _a[1];
    var _b = react_1.useState(), error = _b[0], setError = _b[1];
    var _c = react_1.useState(new ActionState(ActionStates.NotTriggered)), actionState = _c[0], setActionState = _c[1];
    return {
        result: result,
        state: actionState,
        error: error,
        reset: function (resetResult) {
            if (resetResult === void 0) { resetResult = true; }
            if (resetResult) {
                setResult(undefined);
            }
            setError(undefined);
            setActionState(new ActionState(ActionStates.NotTriggered));
        },
        trigger: function (actionDelegate) {
            var args = [];
            for (var _i = 1; _i < arguments.length; _i++) {
                args[_i - 1] = arguments[_i];
            }
            return __awaiter(void 0, void 0, void 0, function () {
                var debounceTimer, actionResult, err_1;
                return __generator(this, function (_a) {
                    switch (_a.label) {
                        case 0:
                            setActionState(new ActionState(ActionStates.Triggered));
                            setError(undefined);
                            if (slowThreshold > 0) {
                                debounceTimer = setTimeout(function () {
                                    setActionState(new ActionState(ActionStates.Slow));
                                }, slowThreshold);
                            }
                            else {
                                setActionState(new ActionState(ActionStates.Slow));
                            }
                            actionResult = result;
                            _a.label = 1;
                        case 1:
                            _a.trys.push([1, 3, 4, 5]);
                            return [4 /*yield*/, actionDelegate.apply(null, args)];
                        case 2:
                            actionResult = _a.sent();
                            setResult(actionResult);
                            setActionState(new ActionState(ActionStates.Complete));
                            return [3 /*break*/, 5];
                        case 3:
                            err_1 = _a.sent();
                            setActionState(new ActionState(ActionStates.Failed));
                            setError(err_1);
                            throw err_1;
                        case 4:
                            if (debounceTimer) {
                                clearTimeout(debounceTimer);
                            }
                            return [7 /*endfinally*/];
                        case 5: return [2 /*return*/, actionResult];
                    }
                });
            });
        },
    };
};
exports.useAsyncAction = useAsyncAction;
var ActionStates;
(function (ActionStates) {
    ActionStates[ActionStates["NotTriggered"] = 0] = "NotTriggered";
    ActionStates[ActionStates["Triggered"] = 1] = "Triggered";
    ActionStates[ActionStates["Slow"] = 2] = "Slow";
    ActionStates[ActionStates["Complete"] = 3] = "Complete";
    ActionStates[ActionStates["Failed"] = 4] = "Failed";
})(ActionStates = exports.ActionStates || (exports.ActionStates = {}));
/**
 * The ActionState class simply represents our state data structure.
 *
 * It essentially translates a 'point in time' status (passed in the constructor)
 * into 3 booleans. This makes is easy to test for the various statuses without
 * comparing a 'state' variable to constants or even an enum.
 */
var ActionState = /** @class */ (function () {
    function ActionState(state) {
        Object.defineProperty(this, "state", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: void 0
        });
        Object.defineProperty(this, "slow", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "triggered", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "complete", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        Object.defineProperty(this, "failed", {
            enumerable: true,
            configurable: true,
            writable: true,
            value: false
        });
        this.state = state;
        this.slow = state == ActionStates.Slow;
        this.triggered = state == ActionStates.Triggered || state == ActionStates.Slow;
        this.complete = state == ActionStates.Complete;
        this.failed = state == ActionStates.Failed;
    }
    return ActionState;
}());
//# sourceMappingURL=useAsyncAction.js.map